import {
  type Component,
  For,
  createMemo,
  createSignal,
  Show,
  onCleanup,
  onMount,
} from "solid-js"
import { Portal } from "solid-js/web"
import type { Worker, WorkType, DateString, WorkerId } from "@eckersdorf/api"
import { getPolishHolidayName } from "../lib/polish-holidays"

interface ScheduleData {
  [hour: string]: WorkType | null
}

interface Props {
  workers: readonly Worker[]
  month: string // YYYY-MM
  schedules: Record<string, Record<string, ScheduleData>> // workerId -> date -> hour -> type
  onCellClick: (workerId: WorkerId, date: DateString, hour: number) => void
  onCellDoubleClick: (workerId: WorkerId, date: DateString, hour: number) => void
  onSetCell: (workerId: WorkerId, date: DateString, hour: number, type: WorkType | null) => void
  onSetFullDay: (workerId: WorkerId, date: DateString, type: WorkType) => void
  onSetShift: (workerId: WorkerId, date: DateString, startHour: number, hours: number, type: WorkType) => void
  onSetHoliday: (date: DateString) => void
  onClearDay: (workerId: WorkerId, date: DateString) => void
  onClearDayAll: (date: DateString) => void
  readonly?: boolean
}

// Hours to display (6:00 - 21:00)
const HOURS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21] as const

// Day names in Polish
const DAY_NAMES: Record<number, string> = {
  0: "Nd",
  1: "Pn",
  2: "Wt",
  3: "Śr",
  4: "Cz",
  5: "Pt",
  6: "So",
}

const DAY_NAMES_FULL: Record<number, string> = {
  0: "Niedziela",
  1: "Poniedziałek",
  2: "Wtorek",
  3: "Środa",
  4: "Czwartek",
  5: "Piątek",
  6: "Sobota",
}

// Work type classes
const WORK_TYPE_CLASSES: Record<WorkType, string> = {
  seller: "bg-seller hover:bg-opacity-90",
  butcher: "bg-butcher hover:bg-opacity-90",
  holiday: "bg-holiday hover:bg-opacity-90",
  vacation: "bg-vacation hover:bg-opacity-90",
  sickLeave: "bg-sickLeave hover:bg-opacity-90",
  wolne: "bg-wolne hover:bg-opacity-90",
}

// Generate days for a month
function getDaysInMonth(monthStr: string): DateString[] {
  const [yearStr, monthNumStr] = monthStr.split("-")
  if (yearStr === undefined || monthNumStr === undefined) {
    return []
  }
  const year = parseInt(yearStr, 10)
  const monthNum = parseInt(monthNumStr, 10)
  const daysInMonth = new Date(year, monthNum, 0).getDate()

  const days: DateString[] = []
  for (let d = 1; d <= daysInMonth; d++) {
    const day = d.toString().padStart(2, "0")
    days.push(`${monthStr}-${day}` as DateString)
  }
  return days
}

function getDayOfWeek(dateStr: DateString): number {
  return new Date(dateStr).getDay()
}

const BellIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
  </svg>
)

const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
  </svg>
)

export const ScheduleGrid: Component<Props> = (props) => {
  const [selectedWorker, setSelectedWorker] = createSignal<WorkerId | null>(null)
  const [contextMenu, setContextMenu] = createSignal<{
    x: number
    y: number
    workerId: WorkerId
    date: DateString
    hour: number
  } | null>(null)

  onMount(() => {
    const closeMenu = () => setContextMenu(null)
    document.addEventListener("click", closeMenu)
    onCleanup(() => document.removeEventListener("click", closeMenu))
  })

  const days = createMemo(() => getDaysInMonth(props.month))

  const getWorkerById = (workerId: WorkerId): Worker | undefined => {
    return props.workers.find(w => w.id === workerId)
  }

  const getCellClass = (workerId: WorkerId, date: DateString, hour: number): string => {
    const hourStr = hour.toString().padStart(2, "0")
    const workType = props.schedules[workerId]?.[date]?.[hourStr] ?? null
    if (workType === null) {
      return "hover:bg-gray-50"
    }
    return WORK_TYPE_CLASSES[workType]
  }

  const isHoliday = (date: DateString) => {
    if (props.workers.length === 0) return false
    // Check if all workers have holiday on hour 12
    return props.workers.every((worker) => {
      const type = props.schedules[worker.id]?.[date]?.["12"]
      return type === "holiday"
    })
  }

  const getWorkerDayStatus = (workerId: WorkerId, date: DateString): WorkType | null => {
    // Check hour 12 for day status
    const type = props.schedules[workerId]?.[date]?.["12"] ?? null
    return type === "vacation" || type === "sickLeave" || type === "wolne" ? type : null
  }

  const getWorkerMonthStats = createMemo(() => {
    const [yearStr, monthStr] = props.month.split("-")
    const year = parseInt(yearStr ?? "2024", 10)
    const month = parseInt(monthStr ?? "1", 10)
    const daysInMonth = new Date(year, month, 0).getDate()

    // Calculate working days for expected hours
    let workingDays = 0
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month - 1, d)
      const dayOfWeek = date.getDay()
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        workingDays++
      }
    }
    const expectedHours = workingDays * 8

    const statsMap = new Map<WorkerId, {
      hoursWorked: number
      hoursRemaining: number
      saturdaysWorked: number
      sundaysWorked: number
    }>()

    for (const worker of props.workers) {
      let hoursWorked = 0
      const saturdaysWorked = new Set<string>()
      const sundaysWorked = new Set<string>()

      const workerSchedule = props.schedules[worker.id] || {}

      for (const [dateStr, daySchedule] of Object.entries(workerSchedule)) {
       if (!daySchedule) continue

       for (const [, type] of Object.entries(daySchedule)) {
         if (type === "seller" || type === "butcher" || type === "vacation" || type === "sickLeave") {
           hoursWorked++

            // Only count weekend stats for actual work (not vacation/sickLeave)
            if (type === "seller" || type === "butcher") {
              const date = new Date(dateStr)
              const dayOfWeek = date.getDay()
              if (dayOfWeek === 6) saturdaysWorked.add(dateStr)
              if (dayOfWeek === 0) sundaysWorked.add(dateStr)
            }
         }
       }
     }

      statsMap.set(worker.id, {
        hoursWorked,
        hoursRemaining: expectedHours - hoursWorked,
        saturdaysWorked: saturdaysWorked.size,
        sundaysWorked: sundaysWorked.size,
      })
    }

    return statsMap
  })

  return (
    <div class="flex flex-col bg-gray-100 min-h-full pb-16">
      <div class="flex flex-col gap-6 p-4">
        <For each={days()}>
          {(date) => {
          const dow = getDayOfWeek(date)
          const isSunday = dow === 0
          const isSaturday = dow === 6
          const dayNum = date.split("-")[2]
          const userSetHoliday = isHoliday(date)
          const polishHolidayName = getPolishHolidayName(date)
          const isPolishPublicHoliday = polishHolidayName !== null

          return (
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden shrink-0">
              <div
                class={`px-4 py-2 text-sm font-semibold border-b border-gray-200 flex items-center gap-2 ${
                  userSetHoliday ? "bg-pink-100 text-pink-900" :
                  (isSunday || isPolishPublicHoliday) ? "bg-red-50 text-red-900" :
                  isSaturday ? "bg-yellow-50 text-yellow-900" : "bg-gray-50 text-gray-700"
                }`}
              >
                <span>
                  {dayNum} {DAY_NAMES[dow]} ({DAY_NAMES_FULL[dow]})
                  {polishHolidayName && <span class="ml-2 font-normal italic">— {polishHolidayName}</span>}
                </span>
                <button
                  class="ml-auto p-1 rounded hover:bg-black/5 text-current transition-colors"
                  onClick={() => props.onSetHoliday(date)}
                  title="Przełącz święto dla wszystkich"
                >
                  <BellIcon />
                </button>
              </div>
              <div class="overflow-x-auto">
                <table class="w-full border-collapse text-xs">
                  <thead>
                    <tr>
                      <th class="sticky left-0 z-10 w-[200px] min-w-[150px] bg-white border-b border-r border-gray-200 p-2 text-left font-medium text-gray-500">
                        {/* Placeholder for worker column header */}
                      </th>
                      <For each={[...HOURS]}>
                        {(hour) => (
                          <th class="border-b border-r border-gray-200 p-1 min-w-[32px] text-center font-normal text-gray-500">
                            {hour.toString().padStart(2, "0")}
                          </th>
                        )}
                      </For>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={props.workers}>
                      {(worker) => {
                        const stats = () => getWorkerMonthStats().get(worker.id)
                        return (
                        <tr
                          class={selectedWorker() === worker.id ? "bg-blue-50" : "bg-white"}
                          onClick={() => setSelectedWorker(worker.id)}
                        >
                          <td
                            class={`sticky left-0 z-10 border-b border-r border-gray-200 p-2 font-medium whitespace-nowrap shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] flex items-center justify-between gap-2 ${
                              selectedWorker() === worker.id
                                ? "bg-blue-50 text-blue-900"
                                : "bg-white text-gray-900"
                            }`}
                            title={`${worker.lastName} ${worker.firstName}\nPrzepracowane: ${stats()?.hoursWorked ?? 0}h\nBrakuje: ${stats()?.hoursRemaining ?? 0}h\nSoboty: ${stats()?.saturdaysWorked ?? 0}\nNiedziele: ${stats()?.sundaysWorked ?? 0}`}
                          >
                            <div class="flex flex-col">
                              <span>{worker.lastName} {worker.firstName}</span>
                              <span class="text-[10px] font-normal text-gray-500">
                                {stats()?.hoursWorked ?? 0}h / {stats()?.hoursRemaining !== undefined && stats()!.hoursRemaining < 0 ? "+" : ""}{Math.abs(stats()?.hoursRemaining ?? 0)}h | Sob: {stats()?.saturdaysWorked ?? 0} | Ndz: {stats()?.sundaysWorked ?? 0}
                              </span>
                            </div>
                            <div class="flex items-center gap-1">
                              <Show when={getWorkerDayStatus(worker.id, date) === "vacation"}>
                                <div class="w-2 h-2 rounded-full bg-vacation" title="Urlop" />
                              </Show>
                              <Show when={getWorkerDayStatus(worker.id, date) === "sickLeave"}>
                                <div class="w-2 h-2 rounded-full bg-sickLeave" title="Chorobowe" />
                              </Show>
                              <Show when={getWorkerDayStatus(worker.id, date) === "wolne"}>
                                <div class="w-2 h-2 rounded-full bg-wolne border border-gray-300" title="Wolne" />
                              </Show>
                              <button
                                class="p-1 rounded hover:bg-black/5 text-gray-400 hover:text-gray-700"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (getWorkerDayStatus(worker.id, date) === "vacation") {
                                    props.onClearDay(worker.id, date)
                                  } else {
                                    props.onSetFullDay(worker.id, date, "vacation")
                                  }
                                }}
                                title={getWorkerDayStatus(worker.id, date) === "vacation" ? "Usuń urlop" : "Ustaw urlop na cały dzień"}
                              >
                                <SunIcon />
                              </button>
                            </div>
                          </td>
                          <For each={[...HOURS]}>
                            {(hour) => (
                              <td
                                class={`border-b border-r border-gray-100 p-0 h-8 transition-colors duration-75 ${getCellClass(
                                  worker.id,
                                  date,
                                  hour,
                                )} ${props.readonly === true ? "cursor-default" : "cursor-pointer"}`}
                                onClick={(e) => {
                                  if (props.readonly !== true) {
                                    e.stopPropagation()
                                    props.onCellClick(worker.id, date, hour)
                                  }
                                }}
                                onContextMenu={(e) => {
                                  if (props.readonly !== true) {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setContextMenu({
                                      x: e.pageX,
                                      y: e.pageY,
                                      workerId: worker.id,
                                      date,
                                      hour,
                                    })
                                  }
                                }}
                                onDblClick={(e) => {
                                  if (props.readonly !== true) {
                                    e.stopPropagation()
                                    props.onCellDoubleClick(worker.id, date, hour)
                                  }
                                }}
                              />
                            )}
                          </For>
                        </tr>
                      )
                      }}
                    </For>
                  </tbody>
                </table>
              </div>
            </div>
          )
        }}
      </For>

      </div>

      <Show when={contextMenu()}>
        {(menu) => (
          <Portal>
            <div
              class="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px] text-sm"
              style={{
                top: `${menu().y}px`,
                left: `${menu().x}px`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div class="px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100 mb-1">
                Wybierz akcję
              </div>
              <button
                class="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                onClick={() => {
                  props.onSetCell(menu().workerId, menu().date, menu().hour, "seller")
                  setContextMenu(null)
                }}
              >
                <div class="w-3 h-3 rounded bg-seller" />
                Sklep (1h)
              </button>
              <button
                class="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                onClick={() => {
                  props.onSetCell(menu().workerId, menu().date, menu().hour, "butcher")
                  setContextMenu(null)
                }}
              >
                <div class="w-3 h-3 rounded bg-butcher" />
                Lada (1h)
              </button>
              <div class="my-1 border-t border-gray-100" />
              <button
                class="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                onClick={() => {
                  const worker = getWorkerById(menu().workerId)
                  const hours = worker?.workingHours ?? 8
                  props.onSetShift(menu().workerId, menu().date, menu().hour, hours, "seller")
                  setContextMenu(null)
                }}
              >
                <div class="w-3 h-3 rounded bg-seller" />
                Sklep ({getWorkerById(menu().workerId)?.workingHours ?? 8}h)
              </button>
              <button
                class="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                onClick={() => {
                  const worker = getWorkerById(menu().workerId)
                  const hours = worker?.workingHours ?? 8
                  props.onSetShift(menu().workerId, menu().date, menu().hour, hours, "butcher")
                  setContextMenu(null)
                }}
              >
                <div class="w-3 h-3 rounded bg-butcher" />
                Lada ({getWorkerById(menu().workerId)?.workingHours ?? 8}h)
              </button>
              <div class="my-1 border-t border-gray-100" />
              <button
                class="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                onClick={() => {
                  props.onSetFullDay(menu().workerId, menu().date, "vacation")
                  setContextMenu(null)
                }}
              >
                <div class="w-3 h-3 rounded bg-vacation" />
                Urlop (cały dzień)
              </button>
              <button
                class="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                onClick={() => {
                  props.onSetFullDay(menu().workerId, menu().date, "sickLeave")
                  setContextMenu(null)
                }}
              >
                <div class="w-3 h-3 rounded bg-sickLeave" />
                Chorobowe (cały dzień)
              </button>
              <button
                class="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                onClick={() => {
                  props.onSetFullDay(menu().workerId, menu().date, "wolne")
                  setContextMenu(null)
                }}
              >
                <div class="w-3 h-3 rounded bg-wolne border border-gray-300" />
                Wolne (cały dzień)
              </button>
              <button
                class="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                onClick={() => {
                  props.onSetHoliday(menu().date)
                  setContextMenu(null)
                }}
              >
                <div class="w-3 h-3 rounded bg-holiday" />
                Święto (wszyscy)
              </button>
              <div class="my-1 border-t border-gray-100" />
              <button
                class="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2 text-red-600"
                onClick={() => {
                  props.onClearDay(menu().workerId, menu().date)
                  setContextMenu(null)
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Wyczyść
              </button>
              <button
                class="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2 text-red-600"
                onClick={() => {
                  props.onClearDayAll(menu().date)
                  setContextMenu(null)
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Wyczyść (wszyscy)
              </button>
            </div>
          </Portal>
        )}
      </Show>

      {/* Legend */}
      <div class="fixed bottom-4 left-1/2 -translate-x-1/2 z-20 bg-white border border-gray-200 p-4 rounded-lg flex justify-center flex-wrap gap-4 text-xs text-gray-600 shadow-lg">
        <div class="flex items-center gap-1">
          <div class="w-4 h-4 rounded bg-seller" />
          <span>Sklep</span>
        </div>
        <div class="flex items-center gap-1">
          <div class="w-4 h-4 rounded bg-butcher" />
          <span>Lada</span>
        </div>
        <div class="flex items-center gap-1">
          <div class="w-4 h-4 rounded bg-holiday" />
          <span>Święto</span>
        </div>
        <div class="flex items-center gap-1">
          <div class="w-4 h-4 rounded bg-vacation" />
          <span>Urlop</span>
        </div>
        <div class="flex items-center gap-1">
          <div class="w-4 h-4 rounded bg-sickLeave" />
          <span>Chorobowe</span>
        </div>
        <div class="flex items-center gap-1">
          <div class="w-4 h-4 rounded bg-wolne border border-gray-300" />
          <span>Wolne</span>
        </div>
      </div>
    </div>
  )
}
